import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface EmptyStateProgress {
  /** How much of the threshold is already met, e.g. 6 days of HRV baseline collected. */
  have: number
  /** The threshold itself, e.g. 21 days needed for a baseline. */
  need: number
  /** Singular or plural unit label rendered after the numbers, e.g. "days". */
  unit: string
}

export interface EmptyStateProps {
  icon: LucideIcon
  title: string
  body?: string
  /**
   * A second, more specific line under `body`: what this panel is waiting on and, where known,
   * when it will have enough. Kept separate from `body` so callers that already pass a reason
   * in `body` can add a concrete unblock condition without rewriting it.
   */
  hint?: string
  /** Renders a thin progress bar plus a "have of need unit" label, for a partial threshold. */
  progress?: EmptyStateProgress
  action?: ReactNode
  className?: string
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  hint,
  progress,
  action,
  className,
}: EmptyStateProps) {
  const pct = progress
    ? Math.max(0, Math.min(100, (progress.have / Math.max(progress.need, 1)) * 100))
    : 0

  return (
    <div className={cn('flex flex-col items-center gap-2 px-6 py-10 text-center', className)}>
      <Icon className="size-8 text-ink-3" aria-hidden="true" />
      <p className="font-display text-body font-semibold text-ink">{title}</p>
      {body && <p className="max-w-[42ch] text-small text-ink-2">{body}</p>}
      {hint && <p className="max-w-[42ch] text-caption text-ink-3">{hint}</p>}
      {progress && (
        <div className="mt-1 flex w-full max-w-[220px] flex-col gap-1">
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-surface-2"
            role="img"
            aria-label={`${progress.have} of ${progress.need} ${progress.unit}`}
          >
            <div className="gd-grow-x h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-caption text-ink-3">
            {progress.have} of {progress.need} {progress.unit}
          </span>
        </div>
      )}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}
